import React, { useEffect, useRef, useState } from 'react';
import { injectIntl, WrappedComponentProps } from 'react-intl';

import { Spacer } from '@tink/core/src/components/atoms/Spacer/Spacer';
import { isString } from '../../../../../utilities/utils';
import { ProviderField } from '../../../../../models/provider';
import {
  calculateProviderInputs,
  PersistedProviderInputsContextType,
  usePersistedProviderInputs as usePersistedProviderInputsImport,
} from '../../PersistedProviderInputsProvider/PersistedProviderInputsProvider';
import { ProviderInputValidator, ValidationValues } from './ProviderInputValidator';
import { getEnterKeyHint, Input, OnChangeProps, ProviderInputState } from './Input/Input';
import { StyledForm, StyledInputsContainer } from './ProviderInputs.styles';

export const PROVIDER_INPUT_FORM_ID = 'providerInputs';

export type SubmitProviderInputsPayload = {
  [inputName: string]: string;
};

export type ProviderInputsPresenterProps = {
  fixedValue?: string;
  providerFields: ProviderField[];
  renderProviderFields?: boolean;
  shouldPersistFormState?: boolean;
  onSubmit: (payload: SubmitProviderInputsPayload) => void;
  setButtonDisabled: (newState: boolean) => void;
  showPinCodeInput?: boolean;
  formId?: string;
  usePersistedProviderInputs?: () => PersistedProviderInputsContextType | undefined;
  shouldTrimInputValues?: boolean;
  children?: React.ReactNode;
};

export type ProviderInputsProps = WrappedComponentProps & ProviderInputsPresenterProps;

export type ProviderInputField = {
  [inputName: string]: ProviderInputState;
};

export const ProviderInputsPresenter = ({
  providerFields,
  onSubmit,
  children,
  fixedValue,
  renderProviderFields = true,
  shouldPersistFormState = false,
  setButtonDisabled,
  showPinCodeInput,
  intl,
  formId = PROVIDER_INPUT_FORM_ID,
  usePersistedProviderInputs = usePersistedProviderInputsImport,
  shouldTrimInputValues,
}: ProviderInputsProps) => {
  const { providerInputs, onProviderInputChange } = usePersistedProviderInputs() || {};
  const [localProviderInputs, setLocalProviderInputs] = useState<ProviderInputField>({});
  const inputRefs = useRef<React.RefObject<HTMLInputElement>[]>([]);

  const focusInput = (index: number) => {
    const inputRef = inputRefs.current[index];

    if (inputRef && inputRef.current) {
      inputRef.current.focus();
    }
  };

  useEffect(() => {
    // We need to ensure that the array has a length before calling `array.every`. From the docs:
    // "In particular, for an empty array, it returns true." https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Array/every#description
    const onlyCheckboxFields =
      providerFields.length && providerFields.every((field) => field.checkbox);

    if (onlyCheckboxFields) {
      // Checkboxes represent a boolean value. As such no validation is required on the field level.
      // Instead we consider the form to be valid if any of the checkboxes are ticked/checked.
      const hasSelectedValue = Object.values(localProviderInputs).some(
        ({ value }) => value === 'true'
      );

      setButtonDisabled(!hasSelectedValue);

      return;
    }

    const isAllInputsValid = !Object.values(localProviderInputs)
      .map((input) => input.providerField.optional || input.isValid)
      .includes(false);

    if (!renderProviderFields || isAllInputsValid) {
      setButtonDisabled(false);

      return;
    }
    setButtonDisabled(true);
  }, [localProviderInputs, renderProviderFields, setButtonDisabled, providerFields]);

  useEffect(
    () => {
      if (providerInputs && shouldPersistFormState) {
        setLocalProviderInputs(providerInputs);
      } else {
        setLocalProviderInputs(calculateProviderInputs(providerFields, fixedValue));
      }
    },
    // Execute recalculation every time `providerFields` change.
    // In supplemental information scenario we want to display always latest 'Security code'.
    [providerFields, fixedValue, providerInputs, shouldPersistFormState]
  );

  const getErrorMessage = ({ errorMessage, errorMessageValues }: ValidationValues) => {
    if (!errorMessage) {
      return '';
    }

    return isString(errorMessage)
      ? errorMessage
      : intl.formatMessage(errorMessage, errorMessageValues);
  };

  const validateInput = ({ providerInputName, value, onFormSubmit }: OnChangeProps) => {
    const inputProvider = localProviderInputs[providerInputName];

    const { onSubmit: onSubmitOperation, onType } = new ProviderInputValidator(
      inputProvider.providerField
    ).validate(value);

    const providerInputState: ProviderInputState = {
      ...(onFormSubmit
        ? {
            ...onSubmitOperation,
            errorMessage: getErrorMessage(onSubmitOperation),
          }
        : {
            ...onType,
            errorMessage: getErrorMessage(onType),
          }),
      value,
      disabled: inputProvider.disabled,
      providerField: inputProvider.providerField,
      showCheckIcon: Boolean(inputProvider.providerField.pattern && onSubmitOperation.isValid),
    };

    if (onFormSubmit && onProviderInputChange && shouldPersistFormState) {
      onProviderInputChange({ providerInputName, value: providerInputState });
    } else {
      setLocalProviderInputs((inputs) => ({
        ...inputs,
        [providerInputName]: providerInputState,
      }));
    }

    return providerInputState;
  };

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (!renderProviderFields) {
      onSubmit({});

      return;
    }

    const validatedInputs = Object.keys(localProviderInputs).map((fieldName) => {
      const inputProvider = localProviderInputs[fieldName];
      const { value } = inputProvider;

      return validateInput({ providerInputName: fieldName, value, onFormSubmit: true });
    });

    const invalidFieldIndex = validatedInputs.map((input) => input.isValid).indexOf(false);

    if (invalidFieldIndex !== -1) {
      // Focus first invalid input.
      focusInput(invalidFieldIndex);

      return;
    }

    // Blur all the inputs to hide software keyboard on mobile devices.
    inputRefs.current.forEach((inputReference) => {
      if (inputReference.current) {
        inputReference.current.blur();
      }
    });

    const inputNameValuePairs: SubmitProviderInputsPayload = {};

    validatedInputs.forEach((field) => {
      inputNameValuePairs[field.providerField.name] = shouldTrimInputValues
        ? field.value.trim()
        : field.value;
    });

    onSubmit(inputNameValuePairs);
  };

  return (
    <StyledForm id={formId} onSubmit={handleSubmit} data-testid={formId}>
      {renderProviderFields && providerFields.length > 0 && (
        <StyledInputsContainer>
          {providerFields.map(({ name: fieldName }, index) => {
            const providerInputState = localProviderInputs[fieldName];

            if (!providerInputState) {
              // the initial field state have not yet been calculated by the effect
              return null;
            }

            // Store a ref, so we can focus the `input` element later.
            const ref = React.createRef<HTMLInputElement>();
            inputRefs.current[index] = ref;

            return (
              <Input
                ref={ref}
                index={index}
                key={
                  /* eslint-disable-next-line react/no-array-index-key */
                  `${fieldName}-${index}`
                }
                onChange={validateInput}
                onEnter={() => focusInput(index + 1)}
                enterKeyHint={getEnterKeyHint(index, providerFields.length)}
                showPinCodeInput={showPinCodeInput}
                providerInputState={providerInputState}
              />
            );
          })}
          <Spacer size="xsmall" />
        </StyledInputsContainer>
      )}
      {/* We rely on `children` to contain a `button` which will submit this form. */}
      {children}
    </StyledForm>
  );
};

export const ProviderInputs = injectIntl(ProviderInputsPresenter);
